<template>
  <div class="fotos-chamado">
    <label class="form-label d-flex align-items-center gap-2">
      Anexos
      <span v-if="enviando" class="spinner-border spinner-border-sm text-primary"></span>
    </label>
    <div class="fotos-chamado-grid">
      <div
        v-for="item in existentes"
        :key="'existente-'+item.id"
        class="fotos-chamado-item"
        :title="item.nome_original || 'Anexo do chamado'"
      >
        <button
          v-if="item.previewUrl"
          type="button"
          class="fotos-chamado-imagem"
          title="Abrir no visualizador"
          @click.prevent="abrirAnexo(item)"
        >
          <img :src="item.previewUrl" :alt="item.nome_original || 'Anexo do chamado'" />
          <span class="fotos-chamado-lupa"><i class="ri-zoom-in-line"></i></span>
        </button>
        <button
          v-else
          type="button"
          class="fotos-chamado-doc"
          :title="tituloAcao(item)"
          @click.prevent="abrirAnexo(item)"
        >
          <i :class="iconeArquivo(item)"></i>
          <span class="fotos-chamado-ext">{{ extensaoDe(item.nome_original) }}</span>
        </button>
        <span
          v-if="item.origem === 'inline'"
          class="fotos-chamado-tag"
          title="Imagem inserida dentro de um comentário"
        >no texto</span>
        <button
          v-if="podeExcluir(item)"
          type="button"
          class="fotos-chamado-remover"
          title="Remover anexo"
          @click.prevent="removerExistente(item)"
        ><i class="ri-close-line"></i></button>
      </div>
      <div
        v-for="(item, index) in pendentes"
        :key="'pendente-'+index"
        class="fotos-chamado-item fotos-chamado-item-pendente"
        :title="item.file.name"
      >
        <img v-if="item.previewUrl" :src="item.previewUrl" :alt="item.file.name" />
        <div v-else class="fotos-chamado-doc">
          <i :class="iconeArquivo({ nome_original: item.file.name, tipo: item.file.type })"></i>
          <span class="fotos-chamado-ext">{{ extensaoDe(item.file.name) }}</span>
        </div>
        <button
          type="button"
          class="fotos-chamado-remover"
          title="Remover anexo"
          @click.prevent="removerPendente(index)"
        ><i class="ri-close-line"></i></button>
      </div>
      <label class="fotos-chamado-adicionar">
        <i class="ri-attachment-2 fs-4"></i>
        <span>Adicionar</span>
        <input
          type="file"
          :accept="accept"
          multiple
          class="d-none"
          @change="onFilesSelected"
        />
      </label>
    </div>
    <div class="form-text">Imagens (JPEG, PNG, WEBP) e documentos (PDF, TXT, DOC, DOCX), até 10MB cada.</div>

    <VisualizadorAnexo
      v-model:visible="visualizadorAberto"
      :nome="visualizador.nome"
      :url="visualizador.url"
      :tipo="visualizador.tipo"
      :carregando="visualizador.carregando"
      @close="fecharVisualizador"
      @baixar="baixarArquivo(visualizador.item)"
    />
  </div>
</template>
<script>
import axios from "axios";
import { aviso, confirmar, erro } from "@/helpers/mirai/custom";
import VisualizadorAnexo from "@/components/mirai/VisualizadorAnexo.vue";

const TIPOS_IMAGEM = ["image/jpeg", "image/png", "image/webp"];
const EXTENSOES_IMAGEM = [".jpg", ".jpeg", ".png", ".webp"];
const EXTENSOES_DOCUMENTO = [".pdf", ".txt", ".doc", ".docx"];
const TAMANHO_MAXIMO = 10 * 1024 * 1024;

// Formatos que o próprio navegador renderiza a partir de um blob — é o que
// permite visualizar sem mandar o arquivo para fora.
const EXTENSOES_INCORPORAVEIS = [".pdf", ".txt", ".csv", ".log", ".json", ".xml", ".md"];
// O tipo MIME do texto precisa ir explícito no blob: o backend costuma devolver
// `application/octet-stream`, e aí o navegador oferece download em vez de exibir.
const MIME_POR_EXTENSAO = {
  ".pdf": "application/pdf",
  ".txt": "text/plain;charset=utf-8",
  ".csv": "text/plain;charset=utf-8",
  ".log": "text/plain;charset=utf-8",
  ".json": "application/json",
  ".xml": "text/xml",
  ".md": "text/plain;charset=utf-8",
};

const extensaoDoNome = (nome = "") => {
  const match = String(nome).toLowerCase().match(/\.[a-z0-9]+$/);
  return match ? match[0] : "";
};

const ehImagem = (file) => TIPOS_IMAGEM.includes(file?.type)
  || EXTENSOES_IMAGEM.includes(extensaoDoNome(file?.name));
const DIMENSAO_MAXIMA_PX = 1600; // maior lado, depois de redimensionar
const QUALIDADE_JPEG = 0.75;

// Reduz qualidade/tamanho da foto no navegador antes de enviar — evita que
// uma foto de celular (comum ter 4-8MB) vá inteira para o servidor. Se a
// compactação falhar ou não render menor, usa o arquivo original.
function comprimirImagem(file) {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      let { width, height } = img;
      const maiorLado = Math.max(width, height);
      if (maiorLado > DIMENSAO_MAXIMA_PX) {
        const escala = DIMENSAO_MAXIMA_PX / maiorLado;
        width = Math.round(width * escala);
        height = Math.round(height * escala);
      }
      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext("2d");
      if (!ctx) {
        resolve(file);
        return;
      }
      ctx.drawImage(img, 0, 0, width, height);
      canvas.toBlob(
        (blob) => {
          if (!blob || blob.size >= file.size) {
            resolve(file);
            return;
          }
          const nomeComprimido = file.name.replace(/\.\w+$/, "") + ".jpg";
          resolve(new File([blob], nomeComprimido, { type: "image/jpeg" }));
        },
        "image/jpeg",
        QUALIDADE_JPEG,
      );
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      resolve(file);
    };
    img.src = url;
  });
}

export default {
  name: "FotosChamado",
  components: { VisualizadorAnexo },
  props: {
    tarefaId: {
      type: [Number, String],
      default: null,
    },
    usuarioId: {
      type: [Number, String],
      default: 0,
    },
  },
  data() {
    return {
      existentes: [],
      pendentes: [],
      // IDs de anexos enviados NESTA sessão da tela. Só eles podem ser
      // excluídos: um anexo já existente (carregado ao abrir o chamado) fica
      // consolidado e não recebe botão de remover.
      idsSessao: new Set(),
      enviando: false,
      visualizadorAberto: false,
      visualizador: {
        carregando: false,
        // 'imagem' | 'incorporado' | 'download'
        tipo: null,
        url: null,
        nome: "",
        item: null,
      },
    };
  },
  watch: {
    tarefaId: {
      immediate: true,
      handler(novoId) {
        if (novoId) {
          this.carregarExistentes();
        }
      },
    },
  },
  beforeUnmount() {
    this.existentes.forEach((item) => item.previewUrl && URL.revokeObjectURL(item.previewUrl));
    this.pendentes.forEach((item) => item.previewUrl && URL.revokeObjectURL(item.previewUrl));
  },
  computed: {
    accept() {
      return [...TIPOS_IMAGEM, ...EXTENSOES_DOCUMENTO].join(",");
    },
  },
  methods: {
    arquivoEhImagem(item) {
      return TIPOS_IMAGEM.includes(item?.tipo)
        || EXTENSOES_IMAGEM.includes(extensaoDoNome(item?.nome_original));
    },
    extensaoDe(nome) {
      return extensaoDoNome(nome).replace(".", "").toUpperCase() || "ARQ";
    },
    iconeArquivo(item) {
      const extensao = extensaoDoNome(item?.nome_original);
      if (extensao === ".pdf") return "ri-file-pdf-line";
      if (extensao === ".txt") return "ri-file-text-line";
      if (extensao === ".doc" || extensao === ".docx") return "ri-file-word-line";
      return "ri-file-line";
    },
    ehIncorporavel(item) {
      return EXTENSOES_INCORPORAVEIS.includes(extensaoDoNome(item?.nome_original));
    },
    tituloAcao(item) {
      if (this.arquivoEhImagem(item)) return "Abrir no visualizador";
      if (this.ehIncorporavel(item)) return "Abrir no visualizador";
      return "Baixar arquivo (formato não abre no navegador)";
    },
    /**
     * Abre o anexo da melhor forma possível.
     *
     * Imagem e formatos que o navegador renderiza (PDF, texto) são exibidos no
     * próprio modal, a partir do blob autenticado. Office não é enviado a
     * visualizador on-line de propósito: serviços como o do Office ou o do
     * Google precisam **buscar o arquivo por uma URL pública**, e o anexo está
     * atrás de sessão — publicá-lo vazaria conteúdo de chamado. Nesses casos o
     * download é o caminho honesto.
     */
    async abrirAnexo(item) {
      if (!item?.id) return;

      const ehImagem = this.arquivoEhImagem(item);
      const incorporavel = this.ehIncorporavel(item);

      this.visualizador = {
        carregando: true,
        tipo: ehImagem ? "imagem" : incorporavel ? "incorporado" : "download",
        url: null,
        nome: item.nome_original || "Anexo",
        item,
      };
      this.visualizadorAberto = true;

      // Imagem já tem blob em memória para a miniatura: reaproveita.
      if (ehImagem && item.previewUrl) {
        this.visualizador.url = item.previewUrl;
        this.visualizador.carregando = false;
        return;
      }

      if (!ehImagem && !incorporavel) {
        this.visualizador.carregando = false;
        await this.baixarArquivo(item);
        return;
      }

      try {
        const resposta = await axios.get(`/tarefa/arquivo/download/${item.id}`, { responseType: "blob" });
        const extensao = extensaoDoNome(item?.nome_original);
        const mime = MIME_POR_EXTENSAO[extensao] || resposta.data?.type || "application/octet-stream";
        const blob = new Blob([resposta.data], { type: mime });
        this.visualizador.url = URL.createObjectURL(blob);
      } catch (e) {
        erro({ title: "Não foi possível abrir o anexo." });
        this.visualizadorAberto = false;
      } finally {
        this.visualizador.carregando = false;
      }
    },
    abrirEmNovaAba() {
      if (!this.visualizador.url) return;
      window.open(this.visualizador.url, "_blank", "noopener");
    },
    fecharVisualizador() {
      // Só revoga o que este visualizador criou; a miniatura da imagem continua
      // sendo usada pelo grid e é liberada no unmount.
      const url = this.visualizador.url;
      const ehMiniatura = this.existentes.some((item) => item.previewUrl && item.previewUrl === url);
      if (url && !ehMiniatura) URL.revokeObjectURL(url);
      // Zerar a flag é obrigatório: sem isso ela fica `true` depois de fechar,
      // o `visible` não muda no próximo clique e o modal nunca reabre.
      this.visualizadorAberto = false;
      this.visualizador = { carregando: false, tipo: null, url: null, nome: "", item: null };
    },
    // O download passa por rota autenticada, então não dá para usar um <a
    // href> direto: busca o blob e dispara o salvamento pelo navegador.
    async baixarArquivo(item) {
      if (!item?.id) return;
      try {
        const resposta = await axios.get(`/tarefa/arquivo/download/${item.id}`, { responseType: "blob" });
        const url = URL.createObjectURL(resposta.data);
        const link = document.createElement("a");
        link.href = url;
        link.download = item.nome_original || "anexo";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      } catch (e) {
        erro({ title: "Não foi possível baixar o anexo." });
      }
    },
    // Recarrega a lista a pedido do formulário (ex.: depois de salvar um
    // comentário que trouxe imagens inline).
    async recarregar() {
      if (!this.tarefaId) return;
      this.existentes.forEach((item) => item.previewUrl && URL.revokeObjectURL(item.previewUrl));
      await this.carregarExistentes();
    },
    async carregarExistentes() {
      try {
        const response = await axios.get("/tarefa/arquivo", { params: { tarefa: this.tarefaId } });
        const lista = Array.isArray(response?.data) ? response.data : [];
        // Só imagem baixa o conteúdo para miniatura; documento vira ícone e o
        // arquivo só é buscado quando alguém clica para baixar.
        const comPreview = await Promise.all(
          lista.map(async (item) => {
            if (!this.arquivoEhImagem(item)) return { ...item, previewUrl: null };
            try {
              const imagem = await axios.get(`/tarefa/arquivo/download/${item.id}`, { responseType: "blob" });
              return { ...item, previewUrl: URL.createObjectURL(imagem.data) };
            } catch (e) {
              return { ...item, previewUrl: null };
            }
          }),
        );
        this.existentes = comPreview;
      } catch (e) {
        console.error("Falha ao carregar anexos do chamado:", e?.response?.data || e);
      }
    },
    async onFilesSelected(event) {
      const arquivos = Array.from(event.target.files || []);
      event.target.value = "";
      for (const file of arquivos) {
        const extensao = extensaoDoNome(file.name);
        const imagem = ehImagem(file);
        if (!imagem && !EXTENSOES_DOCUMENTO.includes(extensao)) {
          aviso({ text: `"${file.name}" não é um tipo aceito. Envie imagem, PDF, TXT, DOC ou DOCX.` });
          continue; // eslint-disable-line no-continue
        }
        // Compactação só faz sentido em imagem — comprimir PDF no canvas o
        // destruiria.
        // eslint-disable-next-line no-await-in-loop
        const processado = imagem ? await comprimirImagem(file) : file;
        if (processado.size > TAMANHO_MAXIMO) {
          aviso({
            text: imagem
              ? `"${file.name}" continua maior que 10MB mesmo após compactar.`
              : `"${file.name}" passa de 10MB.`,
          });
          continue; // eslint-disable-line no-continue
        }
        if (this.tarefaId) {
          // eslint-disable-next-line no-await-in-loop
          await this.uploadImediato(processado);
        } else {
          this.pendentes.push({
            file: processado,
            previewUrl: imagem ? URL.createObjectURL(processado) : null,
          });
        }
      }
    },
    async uploadImediato(file) {
      this.enviando = true;
      // Miniatura só para imagem. Criar object URL de PDF/DOCX fazia o <img>
      // renderizar um arquivo que não é imagem — aparecia o ícone de imagem
      // quebrada com o nome do documento por baixo.
      const previewUrl = ehImagem(file) ? URL.createObjectURL(file) : null;
      try {
        const formData = new FormData();
        formData.append("arquivo", file);
        formData.append("tarefa", this.tarefaId);
        formData.append("us_id", this.usuarioId ?? 0);
        const response = await axios.post("/tarefa/arquivo/upload", formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        this.existentes.push({ ...response.data, previewUrl });
        this.marcarDaSessao(response.data?.id);
      } catch (e) {
        if (previewUrl) URL.revokeObjectURL(previewUrl);
        erro({ title: e.response?.data?.error || "Não foi possível enviar o anexo." });
      } finally {
        this.enviando = false;
      }
    },
    removerPendente(index) {
      const [removido] = this.pendentes.splice(index, 1);
      if (removido?.previewUrl) URL.revokeObjectURL(removido.previewUrl);
    },
    // Marca um anexo como enviado nesta sessão (id vindo do backend após upload).
    marcarDaSessao(id) {
      if (id !== undefined && id !== null) this.idsSessao.add(id);
    },
    // Só permite excluir anexo enviado nesta mesma sessão da tela. Anexos já
    // existentes (carregados ao abrir o chamado) ficam consolidados.
    podeExcluir(item) {
      return this.idsSessao.has(item?.id);
    },
    async removerExistente(item) {
      // Imagem de origem "inline" está referenciada dentro do HTML de um
      // comentário: apagar o arquivo deixa a imagem quebrada no histórico.
      if (item?.origem === "inline") {
        confirmar({
          options: {
            title: "Remover imagem usada em um comentário?",
            text: "Esta imagem aparece dentro do texto de um comentário. Ao remover, ela deixa de carregar no histórico do chamado.",
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: "Sim, remover",
            cancelButtonText: "Cancelar",
            customClass: {
              confirmButton: "btn btn-danger w-xs me-2",
              cancelButton: "btn btn-light w-xs",
            },
            buttonsStyling: false,
          },
          confirmado: () => this.excluirArquivo(item),
        });
        return;
      }

      await this.excluirArquivo(item);
    },
    async excluirArquivo(item) {
      try {
        await axios.delete(`/tarefa/arquivo/${item.id}`);
        this.existentes = this.existentes.filter((atual) => atual.id !== item.id);
        if (item.previewUrl) URL.revokeObjectURL(item.previewUrl);
      } catch (e) {
        erro({ title: e.response?.data?.error || "Não foi possível remover a foto." });
      }
    },
    temPendentes() {
      return this.pendentes.length > 0;
    },
    // Chamado pelo formulário pai depois que o chamado/comentário for salvo
    // com sucesso, para efetivar o upload das fotos escolhidas localmente.
    async enviarPendentes(tarefaId, comentarioId = null) {
      if (!tarefaId || this.pendentes.length === 0) return;
      this.enviando = true;
      try {
        for (const item of this.pendentes) {
          const formData = new FormData();
          formData.append("arquivo", item.file);
          formData.append("tarefa", tarefaId);
          formData.append("us_id", this.usuarioId ?? 0);
          if (comentarioId) formData.append("comentario", comentarioId);
          // eslint-disable-next-line no-await-in-loop
          const response = await axios.post("/tarefa/arquivo/upload", formData, {
            headers: { "Content-Type": "multipart/form-data" },
          });
          this.existentes.push({ ...response.data, previewUrl: item.previewUrl });
          this.marcarDaSessao(response.data?.id);
        }
        this.pendentes = [];
      } catch (e) {
        erro({ title: e.response?.data?.error || "Não foi possível enviar uma ou mais fotos." });
      } finally {
        this.enviando = false;
      }
    },
  },
};
</script>
<style scoped>
.fotos-chamado-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}
.fotos-chamado-item {
  position: relative;
  width: 72px;
  height: 72px;
  border-radius: 0.5rem;
  overflow: hidden;
  border: 1px solid #d8d8d8;
}
.fotos-chamado-item img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}
.fotos-chamado-item-pendente {
  border-style: dashed;
  border-color: #6c757d;
}
.fotos-chamado-remover {
  position: absolute;
  top: 2px;
  right: 2px;
  width: 20px;
  height: 20px;
  line-height: 20px;
  padding: 0;
  border: none;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.6);
  color: #fff;
  font-size: 0.75rem;
}
/* Miniatura clicável: a lupa só aparece no hover para não poluir a grade. */
.fotos-chamado-imagem {
  position: relative;
  display: block;
  width: 100%;
  height: 100%;
  padding: 0;
  border: none;
  background: transparent;
  cursor: zoom-in;
}

.fotos-chamado-imagem img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.fotos-chamado-lupa {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(15, 23, 42, 0.45);
  color: #fff;
  font-size: 1.4rem;
  opacity: 0;
  transition: opacity 0.15s ease;
}

.fotos-chamado-imagem:hover .fotos-chamado-lupa,
.fotos-chamado-imagem:focus-visible .fotos-chamado-lupa {
  opacity: 1;
}

.fotos-chamado-doc {
  width: 100%;
  height: 100%;
  border: none;
  background: var(--vz-light);
  color: var(--vz-secondary-color);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 2px;
  font-size: 1.35rem;
  cursor: pointer;
}

.fotos-chamado-doc:hover {
  color: var(--vz-primary);
}

.fotos-chamado-ext {
  font-size: 0.6rem;
  font-weight: 600;
  letter-spacing: 0.02em;
}

.fotos-chamado-tag {
  position: absolute;
  left: 2px;
  bottom: 2px;
  padding: 0 0.25rem;
  border-radius: 0.25rem;
  background: rgba(15, 23, 42, 0.72);
  color: #fff;
  font-size: 0.6rem;
  line-height: 1.15rem;
}
.fotos-chamado-adicionar {
  width: 72px;
  height: 72px;
  border-radius: 0.5rem;
  border: 1px dashed #adb5bd;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #6c757d;
  font-size: 0.7rem;
  text-align: center;
  gap: 2px;
}
.fotos-chamado-adicionar:hover {
  border-color: #495057;
  color: #495057;
}
[data-bs-theme="dark"] .fotos-chamado-item,
[data-layout-mode="dark"] .fotos-chamado-item {
  border-color: #3c3f42;
}
[data-bs-theme="dark"] .fotos-chamado-adicionar,
[data-layout-mode="dark"] .fotos-chamado-adicionar {
  border-color: #4a4e52;
  color: #adb5bd;
}
</style>
